/**
 * List Groups hooks. All endpoints ride the { success, data } envelope, so
 * every queryFn reads res.data.data. The draft-rule resolve powers the live
 * "→ N match" chip; its query key is the serialized rule, debounced so typing
 * in the builder doesn't fire a request per keystroke.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import api from '@/services/api';

import type {
  CreateListGroupInput,
  ListGroup,
  ListGroupMatchCountsResponse,
  ListGroupResolve,
  ListGroupRuleNode,
  UpdateListGroupInput,
} from './list-groups.types';
import { useListGroupScope } from './scope/list-group-scope';

interface Envelope<T> {
  data: T;
}

const BASE = '/smswiser/list-groups';

export const listGroupKeys = {
  all: ['smswiser', 'list-groups'] as const,
  list: (countryKey?: string) =>
    [...listGroupKeys.all, 'list', countryKey ?? 'all'] as const,
  matchCounts: (countryKey: string) =>
    [...listGroupKeys.all, 'match-counts', countryKey] as const,
  resolve: (id: string, campaignWiserId: number | null = null) =>
    [...listGroupKeys.all, 'resolve', id, campaignWiserId ?? 'country'] as const,
  draft: (countryKey: string, ruleKey: string, campaignWiserId: number | null = null) =>
    [...listGroupKeys.all, 'draft', countryKey, campaignWiserId ?? 'country', ruleKey] as const,
};

export function useListGroups(countryKey?: string) {
  return useQuery({
    queryKey: listGroupKeys.list(countryKey),
    queryFn: async () => {
      const res = await api.get<Envelope<ListGroup[]>>(BASE, {
        params: countryKey ? { countryKey } : undefined,
      });
      return res.data.data;
    },
    staleTime: 30_000,
  });
}

/** Match counts arrive separately from the groups: instant when tracker
 *  stats are warm, otherwise the backend warms them ONCE (single-flight)
 *  and this polls every 4s until `ready`, reading fetch progress meanwhile. */
export function useListGroupMatchCounts(countryKey?: string) {
  return useQuery({
    queryKey: listGroupKeys.matchCounts(countryKey ?? 'none'),
    queryFn: async () => {
      const res = await api.get<Envelope<ListGroupMatchCountsResponse>>(
        `${BASE}/match-counts`,
        { params: { countryKey } },
      );
      return res.data.data;
    },
    enabled: Boolean(countryKey),
    refetchInterval: (q) => (q.state.data?.status === 'warming' ? 4_000 : false),
    // Keep polling while the tab is hidden/occluded — the poll is a ~10ms
    // cache read, and returning to a stale "warming" banner would lie.
    refetchIntervalInBackground: true,
    staleTime: 30_000,
  });
}

/** A saved group's lists, live — as the campaign of the surrounding ListGroupScopeProvider reads them. */
export function useListGroupResolve(id: string | null, enabled = true) {
  const { campaignWiserId } = useListGroupScope();
  return useQuery({
    queryKey: listGroupKeys.resolve(id ?? 'none', campaignWiserId),
    queryFn: async () => {
      const res = await api.get<Envelope<ListGroupResolve>>(
        `${BASE}/${id}/resolve`,
        { params: campaignWiserId ? { campaignWiserId } : undefined },
      );
      return res.data.data;
    },
    enabled: Boolean(id) && enabled,
    staleTime: 0,
    gcTime: 0,
  });
}

/** Resolve an UNSAVED rule (zero writes server-side), 400ms debounced — read
 *  as the ListGroupScopeProvider's campaign reads it (a container editor's group pop-up). */
export function useDraftRuleResolve(
  countryKey: string,
  rule: ListGroupRuleNode | null,
  enabled: boolean,
) {
  const { campaignWiserId } = useListGroupScope();
  const ruleKey = useDebouncedValue(rule ? JSON.stringify(rule) : '', 400);
  return useQuery({
    queryKey: listGroupKeys.draft(countryKey, ruleKey, campaignWiserId),
    queryFn: async () => {
      const res = await api.post<Envelope<ListGroupResolve>>(`${BASE}/resolve-preview`, {
        countryKey,
        ruleJson: JSON.parse(ruleKey) as ListGroupRuleNode,
        ...(campaignWiserId ? { campaignWiserId } : {}),
      });
      return res.data.data;
    },
    enabled: enabled && Boolean(countryKey) && ruleKey.length > 0,
    staleTime: 0,
    gcTime: 0,
  });
}

export function useListGroupMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: listGroupKeys.all });
    // 21/10/2026 — the automation's dry run picks lists through the groups: its query root re-reads after any group change.
    queryClient.invalidateQueries({ queryKey: ['drop-generator'] });
  };

  const create = useMutation({
    mutationFn: async (input: CreateListGroupInput) =>
      (await api.post<Envelope<ListGroup>>(BASE, input)).data.data,
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({ id, ...input }: UpdateListGroupInput & { id: string }) =>
      (await api.patch<Envelope<ListGroup>>(`${BASE}/${id}`, input)).data.data,
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (id: string) => api.delete(`${BASE}/${id}`),
    onSuccess: invalidate,
  });

  return { create, update, remove };
}
